import { cloneDeep } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import {
    LayerType,
    MaterialIdsType,
    type EditingRange,
    type ILayerProps,
    type ITextLayerProps,
    type ITextMark,
} from '@/version-types';
import { pushHistory, RichTextProcessor, updateLayer } from '../utils';

export interface ITextMarkup {
    /**
     * 设置文字划重点
     * @param layerID - 图层 id
     * @param value - 划重点配置
     * @param options - 配置项
     * @returns
     */
    __setTextMark(
        layerID: string,
        value: ITextMark,
        options?: {
            range?: EditingRange | null;
        },
    ): { layer: ILayerProps; range: EditingRange | null } | void;

    /**
     * 取消文字划重点
     * @param layerID - 图层 id
     * @param options - 编辑范围
     * @returns
     */
    __cancelTextMark(
        layerID: string,
        options?: {
            range?: EditingRange | null;
        },
    ): { layer: ILayerProps; range: EditingRange | null } | void;

    /**
     * 更新文字划重点
     * @param layerID - 图层 id
     * @param value - 划重点配置
     * @param options - 配置项
     * @returns
     */
    __updateTextMark(
        layerID: string,
        value: ITextMark,
        options?: {
            range?: EditingRange | null;
            originLayer?: ILayerProps;
        },
    ): { layer: ILayerProps; range: EditingRange | null } | void;
}

export class TextMarkup implements ITextMarkup {
    constructor(private framework: IFramework) {}

    private updateMaterialIDs(layer: ILayerProps) {
        const prevMaterialIDMap = layer.materialIds || {};

        const layerData = layer.data as ITextLayerProps;
        const { textMark, textContents = [] } = layerData;

        const nextTextMarkMaterialIDs: string[] = [];

        if (textMark) {
            nextTextMarkMaterialIDs.push(textMark.materialId);
        }

        if (textContents.length > 0) {
            textContents.forEach((item) => {
                if (item.textMark && !nextTextMarkMaterialIDs.includes(item.textMark.materialId)) {
                    nextTextMarkMaterialIDs.push(item.textMark.materialId);
                }
            });
        }

        const nextMaterialIDMap = cloneDeep(prevMaterialIDMap);
        if (nextTextMarkMaterialIDs.length > 0) {
            nextMaterialIDMap[MaterialIdsType.TEXT_MARK] = nextTextMarkMaterialIDs;
        } else {
            delete nextMaterialIDMap[MaterialIdsType.TEXT_MARK];
        }

        return nextMaterialIDMap;
    }

    private set(layerID: string, value: ITextMark | undefined, options: { range: EditingRange | null }) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        const { shapeExtension, textContents: prevTextContents = [] } = prevLayerData;

        if (shapeExtension) return;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: EditingRange | null = range;

        if (value != null) {
            nextLayerData = { ...nextLayerData, textBackground: [] }; // 划重点和背景特效互斥
        }

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(prevLayerData, range, {
                key: 'textMark',
                value,
            });
            const nextTextContents = result.textContents;

            if (JSON.stringify(prevTextContents) === JSON.stringify(nextTextContents)) return;

            nextLayerData = { ...nextLayerData, textMark: null, textContents: nextTextContents };
            nextRange = result.range;
        } else {
            const { textMark: prevTextMark } = prevLayerData;
            if (value) {
                if (prevTextMark && prevTextMark.materialId === value.materialId) return;
            } else {
                if (!prevTextMark && !prevTextContents.some((item) => !!item.textMark)) return;
            }

            nextLayerData = { ...prevLayerData, textMark: value };

            if (prevTextContents.length > 0) {
                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    textMark: value,
                }));

                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };
        const nextMaterialIDMap = this.updateMaterialIDs(nextLayer);
        nextLayer.materialIds = nextMaterialIDMap;

        updateLayer(this.framework, nextLayer);
        pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].mark);

        return { layer: nextLayer, range: nextRange };
    }

    __setTextMark(layerID: string, value: ITextMark, options: { range: EditingRange | null }) {
        return this.set(layerID, value, options);
    }

    __cancelTextMark(layerID: string, options: { range: EditingRange | null }) {
        return this.set(layerID, undefined, options);
    }

    __updateTextMark(
        layerID: string,
        value: ITextMark,
        options: { range?: EditingRange | null; originLayer?: ILayerProps } = {},
    ) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!prevLayer || prevLayer.layerType !== LayerType.TEXT_LAYER) return;

        const { originLayer } = options;
        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        const { textContents: prevTextContents = [] } = prevLayerData;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };
        let nextRange: EditingRange | null = range;

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(prevLayerData, range, {
                key: 'textMark',
                value,
            });

            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            nextLayerData = { ...nextLayerData, textMark: value };

            if (prevTextContents.length > 0) {
                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    textMark: value,
                }));

                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            }
        }

        const nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        updateLayer(this.framework, nextLayer);

        if (originLayer) {
            pushHistory(this.framework, originLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].mark);
        }

        return { layer: nextLayer, range: nextRange };
    }
}
